import {
  PortalArticleTableColumn,
  PortalArticleTableItem,
  PortalArticleTableRow,
} from '../../../models/PortalArtigoModel'

export const PORTAL_TABLE_MAX_COLUMNS = 8
export const PORTAL_TABLE_MAX_ROWS = 200

export function createDefaultPortalTable(createId: (prefix: string) => string): PortalArticleTableItem {
  const columns = Array.from({ length: 3 }, (_, index) => createPortalTableColumn(createId, index))
  return {
    type: 'table',
    id: createId('table'),
    name: '',
    columns,
    rows: Array.from({ length: 2 }, () => createPortalTableRow(columns, createId)),
    export: { enabled: true },
  }
}

export function createPortalTableColumn(
  createId: (prefix: string) => string,
  index: number
): PortalArticleTableColumn {
  return {
    id: createId('table-column'),
    header: '',
    style: 'default',
    align: 'left',
    width: 'auto',
  }
}

export function createPortalTableRow(
  columns: PortalArticleTableColumn[],
  createId: (prefix: string) => string
): PortalArticleTableRow {
  return {
    id: createId('table-row'),
    cells: Object.fromEntries(columns.map((column) => [column.id, ''])),
  }
}

export function clonePortalTable(
  table: PortalArticleTableItem,
  createId: (prefix: string) => string
): PortalArticleTableItem {
  const columnIds = new Map<string, string>()
  const columns = table.columns.map((column) => {
    const id = createId('table-column')
    columnIds.set(column.id, id)
    return { ...column, id }
  })

  return {
    ...table,
    id: createId('table'),
    columns,
    rows: table.rows.map((row) => ({
      ...row,
      id: createId('table-row'),
      cells: Object.fromEntries(
        table.columns.map((column) => [
          columnIds.get(column.id) as string,
          typeof row.cells[column.id] === 'string' ? row.cells[column.id] : '',
        ])
      ),
    })),
    export: table.export ? { ...table.export } : { enabled: true },
  }
}

export function normalizePortalTable(table: PortalArticleTableItem): PortalArticleTableItem {
  const columns = Array.isArray(table.columns)
    ? table.columns.map((column) => ({
        ...column,
        header: typeof column.header === 'string' ? column.header : '',
        style: column.style || 'default',
        align: column.align || 'left',
        width: column.width || 'auto',
      }))
    : []

  return {
    ...table,
    name: typeof table.name === 'string' ? table.name : '',
    columns,
    rows: Array.isArray(table.rows)
      ? table.rows.map((row) => ({
          ...row,
          cells: Object.fromEntries(
            columns.map((column) => [
              column.id,
              typeof row.cells?.[column.id] === 'string' ? row.cells[column.id] : '',
            ])
          ),
        }))
      : [],
    export: {
      ...table.export,
      enabled: table.export?.enabled !== false,
      fileName: table.export?.fileName?.trim() || undefined,
    },
  }
}

export function hasPortalTableGridContent(table: PortalArticleTableItem) {
  return (
    table.columns.some((column) => column.header.trim()) ||
    table.rows.some((row) => Object.values(row.cells).some((cell) => cell.trim()))
  )
}
